import { isAxiosError } from 'axios';
import type { FieldPath, UseFormSetError } from 'react-hook-form';
import type { ClinicCreateFormData } from './clinicFormSchema';

type SectionPrefix =
  | 'clinicInfo'
  | 'clinicPaymentInfo'
  | 'authorizedReceipt'
  | 'receiptFeeTransfer'
  | 'otherInfo';

const KEY_SECTIONS: ReadonlyArray<{ prefix: SectionPrefix; keys: readonly string[] }> = [
  {
    prefix: 'clinicInfo',
    keys: [
      'clinic_id',
      'name',
      'name_kana',
      'available_from',
      'available_to',
      'sub_number',
      'postal_code',
      'prefecture_id',
      'address1',
      'address2',
      'address3',
      'telephone',
      'fax',
      'mobile',
      'email',
      'open_time_from',
      'open_time_to',
      'open_week',
    ],
  },
  {
    prefix: 'clinicPaymentInfo',
    keys: [
      'bank_payment_type_id',
      'bank_code',
      'bank_type_id',
      'bank_name',
      'bank_name_kana',
      'bank_name_suffix',
      'bank_branch_code',
      'bank_branch_type_id',
      'bank_branch_name',
      'bank_branch_name_kana',
      'branch_branch_name_suffix',
      'bank_account_type_id',
      'account_number',
      'account_name',
      'account_name_kana',
    ],
  },
  {
    prefix: 'authorizedReceipt',
    keys: ['representative_name', 'representative_address', 'representative_telephone'],
  },
  {
    prefix: 'receiptFeeTransfer',
    keys: [
      'transfer_bank_code',
      'transfer_bank_name',
      'transfer_bank_name_kana',
      'transfer_bank_branch_code',
      'transfer_bank_branch_name',
      'transfer_bank_branch_name_kana',
      'transfer_bank_account_type_id',
      'transfer_bank_account_number',
      'transfer_bank_account_name',
      'transfer_bank_account_name_kana',
      'billing_company_code',
      'customer_code',
      'is_excluded_transfer',
      'is_required_billing',
    ],
  },
  {
    prefix: 'otherInfo',
    keys: [
      'company_name',
      'clinic_rank',
      'payment_type',
      'claim_start_yearmonth',
      'claim_end_yearmonth',
      'monthly_fee',
      'is_company',
      'sales_staff_id',
      'remarks',
    ],
  },
];

function mapApiKeyToFormPath(key: string): FieldPath<ClinicCreateFormData> | null {
  for (const { prefix, keys } of KEY_SECTIONS) {
    if (keys.includes(key)) {
      return `${prefix}.${key}` as FieldPath<ClinicCreateFormData>;
    }
  }
  return null;
}

/**
 * Maps Laravel-style flat validation errors (422) onto nested clinic information form fields.
 */
export function applyClinicInformationApiValidationErrors(
  setError: UseFormSetError<ClinicCreateFormData>,
  error: unknown
): void {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return;
  }

  const data = error.response.data as { errors?: Record<string, string[] | string> } | undefined;
  const bag = data?.errors;
  if (!bag || typeof bag !== 'object') {
    return;
  }

  for (const [key, raw] of Object.entries(bag)) {
    const messages = Array.isArray(raw) ? raw : [String(raw)];
    const message = messages.find(m => typeof m === 'string' && m.trim() !== '');
    if (!message) {
      continue;
    }
    const path = mapApiKeyToFormPath(key);
    if (path) {
      setError(path, { type: 'server', message });
    }
  }
}

/** Must match backend `ClinicInformationApplicablePeriodOverlapRule::MESSAGE`. */
export const CLINIC_INFORMATION_PERIOD_OVERLAP_API_MESSAGE = '適用期間が重複しています。';

/**
 * True when API returned 422 with the applicable-period overlap message on `available_from`
 * (shown under 適用期間 on the form; suppress duplicate toast).
 */
export function isClinicInformationPeriodOverlapApiError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return false;
  }

  const data = error.response.data as { errors?: Record<string, string[] | string> } | undefined;
  const raw = data?.errors?.available_from;
  const messages = Array.isArray(raw) ? raw : raw !== undefined ? [String(raw)] : [];
  return messages.some(
    m => typeof m === 'string' && m.trim() === CLINIC_INFORMATION_PERIOD_OVERLAP_API_MESSAGE
  );
}

/** Must match create/update `clinic_id.exists` on `ClinicInformationCreateRequest` / `ClinicInformationUpdateRequest`. */
export const CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE =
  '指定された治療院は存在しません。他の治療院を選んでください。';

export function isClinicInformationClinicIdNotExistsApiError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 422) {
    return false;
  }

  const data = error.response.data as { errors?: Record<string, string[] | string> } | undefined;
  const raw = data?.errors?.clinic_id;
  const messages = Array.isArray(raw) ? raw : raw !== undefined ? [String(raw)] : [];
  return messages.some(
    m => typeof m === 'string' && m.trim() === CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE
  );
}
